import { useState } from 'react';
import * as rrd from 'react-router-dom';

import { ACCENT_COLOR } from '@/constants';
import { PlatformIcon } from '@/components';
import { useConversionDetails } from '@/hooks';

import { STATUS_CONFIG } from '../helpers';

import PlaylistDetail from './PlaylistDetail';
import { formatDate, TYPE_LABELS } from './helpers';

const { useNavigate } = rrd.default ?? rrd;

const ConversionRow = ({ conversion, onUnauthorized }) => {
  const {
    id,
    type,
    status,
    source,
    target,
    createdAt,
    playlistName,
    isTargetPrivate,
    sourceAccountId,
    targetAccountId,
    sourcePlaylistCount,
    targetPlaylistCount,
    skippedPlaylistCount,
  } = conversion;

  const navigate = useNavigate();

  const [expanded, setExpanded] = useState(false);

  const {
    getDetails,
    data: details,
    error: detailsError,
    isLoading: detailsLoading,
  } = useConversionDetails({ onUnauthorized });

  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.pending;

  const commonIconProps = {
    ariaHidden: true,
    className: 'flex-shrink-0',
  };

  const isRetryable = status === 'failure' || status === 'incomplete';

  const handleRetry = (e) => {
    e.stopPropagation();

    navigate(`/retry/${id}`, {
      state: {
        type,
        source,
        target,
        isTargetPrivate,
        sourceAccountId,
        targetAccountId,
      },
    });
  };

  const handleToggle = () => {
    if (expanded) {
      setExpanded(false);
      return;
    }

    setExpanded(true);

    if (!details) getDetails(id);
  };

  return (
    <div className="border rounded mb-3">
      <div
        role="button"
        onClick={handleToggle}
        className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 p-3 cursor-pointer"
      >
        <div className="d-flex align-items-center gap-2">
          {source && <PlatformIcon {...commonIconProps} platform={source} />}

          <i
            aria-hidden="true"
            className="fa fa-arrow-right"
            style={{ fontSize: '0.9rem', color: ACCENT_COLOR }}
          />

          {target && <PlatformIcon {...commonIconProps} platform={target} />}

          <div className="ms-1">
            <span className="fw-semibold">
              {playlistName
                ? sourcePlaylistCount > 1
                  ? `${playlistName} + ${sourcePlaylistCount - 1} more`
                  : playlistName
                : `${sourcePlaylistCount} ${sourcePlaylistCount === 1 ? 'playlist' : 'playlists'}`}
            </span>

            <div className="d-flex align-items-center gap-2 mt-1">
              <small className="text-muted">{TYPE_LABELS[type] || type}</small>

              {conversion.retryOf && (
                <span
                  className="badge rounded-pill"
                  style={{ fontSize: '0.65rem', background: ACCENT_COLOR }}
                >
                  <i aria-hidden="true" className="fas fa-redo me-1" />
                  Retry
                </span>
              )}

              {sourcePlaylistCount > 1 && (
                <small className="text-muted">
                  {targetPlaylistCount + (skippedPlaylistCount || 0)}/
                  {sourcePlaylistCount} playlists
                </small>
              )}
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center gap-3 flex-shrink-0">
          {isRetryable && (
            <button
              onClick={handleRetry}
              style={{ fontSize: '0.75rem' }}
              className="btn btn-sm btn-outline-custom btn-outline-accent py-0 px-2"
            >
              <i aria-hidden="true" className="fas fa-redo me-1" />
              Retry
            </button>
          )}

          <small
            title={statusConfig.title.conversion}
            className={`d-flex align-items-center gap-1 ${statusConfig.className}`}
          >
            <i
              aria-hidden="true"
              style={{ fontSize: '0.75rem' }}
              className={`fas ${statusConfig.icon}`}
            />
            {statusConfig.label}
          </small>

          <small className="text-muted">{formatDate(createdAt)}</small>

          <i
            aria-hidden="true"
            style={{ fontSize: '0.75rem' }}
            className={`fas fa-chevron-${expanded ? 'up' : 'down'} text-muted`}
          />
        </div>
      </div>

      {expanded && (
        <div
          className="border-top"
          style={{ maxHeight: '300px', overflowY: 'auto' }}
        >
          {detailsLoading && (
            <div className="text-center py-3">
              <i
                aria-hidden="true"
                style={{ fontSize: '0.9rem' }}
                className="fas fa-spinner fa-spin text-accent"
              />
            </div>
          )}

          {detailsError && (
            <div className="text-center py-3">
              <small className="text-danger">{detailsError}</small>
            </div>
          )}

          {details &&
            details.playlists.map((playlist) => (
              <PlaylistDetail playlist={playlist} key={playlist.id} />
            ))}
        </div>
      )}
    </div>
  );
};

export default ConversionRow;
